import { useContext, useEffect, useMemo, useState } from "react";
import { Select, MenuItem, TextField, Box } from "@mui/material";
import { LanguageContext } from "App";

const NewQuickDate = ({ from, to, setFrom, setTo, placeholder, reset, future = false }) => {
  const { translations } = useContext(LanguageContext);
  const [quickDate, setQuickDate] = useState("All Time");

  const handleChange = (value) => {
    switch (value) {
      case "Today":
        setFrom(new Date().toISOString().slice(0, 10));
        setTo(new Date().toISOString().slice(0, 10));
        break;
      case "Yesterday":
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        setFrom(yesterday.toISOString().slice(0, 10));
        setTo(yesterday.toISOString().slice(0, 10));
        break;
      case "Tomorrow":
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setFrom(tomorrow.toISOString().slice(0, 10));
        setTo(tomorrow.toISOString().slice(0, 10));
        break;
      case "This Week":
        const startOfWeek = new Date();
        startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
        const endOfWeek = new Date();
        endOfWeek.setDate(endOfWeek.getDate() - startOfWeek.getDay() + 6);
        setFrom(startOfWeek.toISOString().slice(0, 10));
        setTo(endOfWeek.toISOString().slice(0, 10));
        break;
      case "Next Week":
        const startOfNextWeek = new Date();
        startOfNextWeek.setDate(startOfNextWeek.getDate() - startOfNextWeek.getDay() + 7);
        const endOfNextWeek = new Date();
        endOfNextWeek.setDate(endOfNextWeek.getDate() - startOfNextWeek.getDay() + 13);
        setFrom(startOfNextWeek.toISOString().slice(0, 10));
        setTo(endOfNextWeek.toISOString().slice(0, 10));
        break;
      case "This Month":
        const startOfMonth = new Date();
        startOfMonth.setDate(1);
        const endOfMonth = new Date(startOfMonth.getFullYear(), startOfMonth.getMonth() + 1, 0);
        setFrom(startOfMonth.toISOString().slice(0, 10));
        setTo(endOfMonth.toISOString().slice(0, 10));
        break;
      case "Next Month":
        const startOfNextMonth = new Date();
        startOfNextMonth.setMonth(startOfNextMonth.getMonth() + 1);
        startOfNextMonth.setDate(1);
        const endOfNextMonth = new Date(
          startOfNextMonth.getFullYear(),
          startOfNextMonth.getMonth() + 1,
          0
        );
        setFrom(startOfNextMonth.toISOString().slice(0, 10));
        setTo(endOfNextMonth.toISOString().slice(0, 10));
        break;
      case "This Year":
        const startOfYear = new Date();
        startOfYear.setMonth(0);
        startOfYear.setDate(1);
        const endOfYear = new Date(startOfYear.getFullYear(), 11, 31);
        setFrom(startOfYear.toISOString().slice(0, 10));
        setTo(endOfYear.toISOString().slice(0, 10));
        break;
      case "Last Week":
        const startOfLastWeek = new Date();
        startOfLastWeek.setDate(startOfLastWeek.getDate() - startOfLastWeek.getDay() - 7);
        const endOfLastWeek = new Date();
        endOfLastWeek.setDate(endOfLastWeek.getDate() - startOfLastWeek.getDay() + 0);
        setFrom(startOfLastWeek.toISOString().slice(0, 10));
        setTo(endOfLastWeek.toISOString().slice(0, 10));
        break;
      case "Last Month":
        const startOfLastMonth = new Date();
        startOfLastMonth.setMonth(startOfLastMonth.getMonth() - 1);
        startOfLastMonth.setDate(1);
        const endOfLastMonth = new Date(
          startOfLastMonth.getFullYear(),
          startOfLastMonth.getMonth() + 1,
          0
        );
        setFrom(startOfLastMonth.toISOString().slice(0, 10));
        setTo(endOfLastMonth.toISOString().slice(0, 10));
        break;
      case "All Time":
        setFrom("");
        setTo("");
        break;
      default:
        setFrom("");
        setTo("");
    }
  };

  useEffect(() => {
    if (reset) {
      setQuickDate("All Time");
      setFrom("");
      setTo("");
    }
  }, [reset, setFrom, setTo]);

  const futureDates = [
    { value: "Today", label: translations.qdToday },
    { value: "Yesterday", label: translations.qdYesterday },
    { value: "Tomorrow", label: translations.qdTomorrow },
    { value: "This Week", label: translations.qdThisWeek },
    { value: "Next Week", label: translations.qdNextWeek },
    { value: "This Month", label: translations.qdThisMonth },
    { value: "Next Month", label: translations.qdNextMonth },
    { value: "This Year", label: translations.qdThisYear },
  ];

  const normalDates = [
    { value: "Today", label: translations.qdToday },
    { value: "Tomorrow", label: translations.qdTomorrow },
    { value: "This Week", label: translations.qdThisWeek },
    { value: "Last Week", label: translations.qdLastWeek },
    { value: "This Month", label: translations.qdThisMonth },
    { value: "Last Month", label: translations.qdLastMonth },
    { value: "This Year", label: translations.qdThisYear },
  ];

  const allTime = { value: "All Time", label: translations.qdAllTime };
  const custom = { value: "Custom", label: translations.qdAllCustom };

  const datesToShow = useMemo(() => (future ? futureDates : normalDates), [future]);

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
      <Select
        value={quickDate}
        onChange={(e) => {
          console.log(e.target.value);
          setQuickDate(e.target.value);
          handleChange(e.target.value);
        }}
        sx={{ minWidth: 100, padding: "12px" }}
      >
        {[allTime, ...datesToShow, custom].map(({ value, label }) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </Select>
      <TextField
        type="date"
        value={from}
        onChange={(e) => {
          setQuickDate("Custom");
          setFrom(e.target.value);
        }}
        sx={{ minWidth: 150 }}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        type="date"
        value={to}
        onChange={(e) => {
          setQuickDate("Custom");
          setTo(e.target.value);
        }}
        sx={{ minWidth: 150 }}
        InputLabelProps={{ shrink: true }}
      />
    </Box>
  );
};

export default NewQuickDate;
